import type { CompoundClause, TerminalClause } from '@atlaskit/jql-ast/ast/clause';
import type { Operand } from '@atlaskit/jql-ast/ast/operand';
import type { OperatorValue } from '@atlaskit/jql-ast/ast/operator';
import type { OrderByDirectionValue } from '@atlaskit/jql-ast/ast/order-by';
import type { Predicate, PredicateOperatorValue } from '@atlaskit/jql-ast/ast/predicate';
import { CLAUSE_TYPE_COMPOUND, CLAUSE_TYPE_TERMINAL } from '@atlaskit/jql-ast/clause';
import { COMPOUND_OPERATOR_AND, COMPOUND_OPERATOR_OR } from '@atlaskit/jql-ast/compound';
import { JastBuilder } from '@atlaskit/jql-ast/jast-builder';
import { walkAST } from '@atlaskit/jql-ast/jast-walker';
import {
	OPERAND_TYPE_FUNCTION,
	OPERAND_TYPE_KEYWORD,
	OPERAND_TYPE_LIST,
	OPERAND_TYPE_VALUE,
} from '@atlaskit/jql-ast/operand';
import { ORDER_BY_DIRECTION_ASC } from '@atlaskit/jql-ast/order-by';

export type ParsedJqlPredicate = {
	operator: PredicateOperatorValue;
	value: string[];
};

export type ParsedJqlClause = {
	field: string;
	operator: OperatorValue;
	value: string[];
	// Only set when the clause's value can't be resolved as normal operand values (currently only
	// CHANGED clauses, whose operand is always undefined) — carries the raw JQL substring instead
	// of dropping the clause entirely.
	rawText?: string;
	// BY user / DURING window scoping carried by WAS/WAS IN/WAS NOT/WAS NOT IN clauses.
	predicates?: ParsedJqlPredicate[];
};

export type ParsedJqlSort = {
	field: string;
	direction: OrderByDirectionValue;
};

export type ParsedJql = {
	clauses: ParsedJqlClause[];
	// True when the JQL contains structure (NOT, or nested AND/OR grouping) that can't be
	// flattened into a flat list of filter chips without misrepresenting the query.
	isComplex: boolean;
	// The primary ORDER BY field, if any. A JQL query can technically specify multiple sort
	// fields (e.g. "ORDER BY a ASC, b DESC"), but this view only surfaces a single sort chip.
	// When more than one sort field is present, `isComplex` is set instead so the caller falls
	// back to the raw JQL rather than silently dropping the extra sort criteria.
	sortBy: ParsedJqlSort | undefined;
};

// Keyword sentinel that the builder emits for the JQL `EMPTY` keyword operand.
const EMPTY_KEYWORD_VALUE = 'EMPTY';

const singleOperandToValue = (operand: Operand): string | undefined => {
	switch (operand.operandType) {
		case OPERAND_TYPE_VALUE:
			return operand.value;
		case OPERAND_TYPE_KEYWORD:
			return EMPTY_KEYWORD_VALUE;
		case OPERAND_TYPE_FUNCTION: {
			const args = operand.arguments.map((argument) => argument.value).join(', ');
			return `${operand.function.value}(${args})`;
		}
		default:
			// Nested list operands are not valid JQL, so there is nothing to extract here.
			return undefined;
	}
};

const operandToValues = (operand: Operand): string[] => {
	if (operand.operandType !== OPERAND_TYPE_LIST) {
		const value = singleOperandToValue(operand);
		return value === undefined ? [] : [value];
	}

	const values: string[] = [];
	for (const value of operand.values) {
		const resolved = singleOperandToValue(value);
		if (resolved !== undefined) {
			values.push(resolved);
		}
	}
	return values;
};

export const parseViewJql = (jql: string): ParsedJql => {
	const trimmedJql = jql.trim();
	if (trimmedJql === '') {
		return { clauses: [], isComplex: false, sortBy: undefined };
	}

	const jast = new JastBuilder().build(trimmedJql);
	const { query } = jast;
	if (!query) {
		return { clauses: [], isComplex: false, sortBy: undefined };
	}

	const clauses: ParsedJqlClause[] = [];
	let isComplex = false;
	walkAST(
		{
			enterTerminalClause: (clause) => {
				if (clause.operator && clause.operand) {
					const predicates: ParsedJqlPredicate[] = clause.predicates.map(
						(predicate: Predicate) => ({
							operator: predicate.operator.value,
							value: predicate.operand ? operandToValues(predicate.operand) : [],
						}),
					);
					clauses.push({
						field: clause.field.value,
						operator: clause.operator.value,
						value: operandToValues(clause.operand),
						...(predicates.length > 0 ? { predicates } : {}),
					});
				} else if (clause.operator) {
					// CHANGED clauses always have an undefined operand (@atlaskit/jql-ast never resolves
					// one for them), so they'd otherwise be silently dropped. Fall back to the raw JQL
					// substring rather than losing the clause entirely.
					const [start, end] = clause.position ?? [0, -1];
					clauses.push({
						field: clause.field.value,
						operator: clause.operator.value,
						value: [],
						rawText: trimmedJql.slice(start, end + 1),
					});
				}
			},
			// A NOT-wrapped clause can never be represented as flat chips.
			enterNotClause: () => {
				isComplex = true;
			},
			// A flat AND/OR of terminal clauses (e.g. "a = 1 AND b = 2 AND c = 3", or
			// "a = 1 OR a = 2") still flattens fine into chips, and so does a redundant
			// same-operator grouping (e.g. "(a = 1 AND b = 2) AND c = 3" is just "a = 1 AND
			// b = 2 AND c = 3"). A compound clause that nests a compound clause with a DIFFERENT
			// operator (e.g. "(a = 1 OR b = 2) AND c = 3") changes meaning when flattened, and so
			// does a FLAT OR across different fields (e.g. "a = 1 OR b = 2") — that also stops
			// meaning the same thing once flattened into stacked chips, which read as AND. A flat
			// OR of the SAME field (e.g. "a = 1 OR a = 2") is unaffected and stays non-complex. A
			// flat AND repeating the SAME field (e.g. "a = 1 AND a = 2") flattens into a chip
			// group visually identical to the OR case, but the semantics differ — AND across
			// equal/incompatible values is a contradictory filter — so that case is complex too.
			enterCompoundClause: (compoundClause) => {
				const hasNestedDifferentOperator = compoundClause.clauses.some(
					(clause) =>
						clause.clauseType === CLAUSE_TYPE_COMPOUND &&
						(clause as CompoundClause).operator.value !== compoundClause.operator.value,
				);

				const terminalFields = compoundClause.clauses
					.filter((clause) => clause.clauseType === CLAUSE_TYPE_TERMINAL)
					.map((clause) => (clause as TerminalClause).field.value.toLowerCase());

				const hasFlatOrAcrossDifferentFields =
					compoundClause.operator.value === COMPOUND_OPERATOR_OR &&
					new Set(terminalFields).size > 1;

				const hasFlatAndAcrossSameField =
					compoundClause.operator.value === COMPOUND_OPERATOR_AND &&
					new Set(terminalFields).size < terminalFields.length;

				if (
					hasNestedDifferentOperator ||
					hasFlatOrAcrossDifferentFields ||
					hasFlatAndAcrossSameField
				) {
					isComplex = true;
				}
			},
		},
		jast,
	);

	const primarySortField = query.orderBy?.fields[0];
	const sortBy: ParsedJqlSort | undefined = primarySortField
		? {
				field: primarySortField.field.value,
				// An omitted direction (e.g. "ORDER BY created") means JQL's default of ascending.
				direction: primarySortField.direction?.value ?? ORDER_BY_DIRECTION_ASC,
			}
		: undefined;

	// More than one ORDER BY field can't be represented by the single-field sort chip without
	// silently dropping the extra criteria, so fall back to raw JQL for the whole query.
	if ((query.orderBy?.fields.length ?? 0) > 1) {
		isComplex = true;
	}

	return { clauses, isComplex, sortBy };
};
